import { asNullableText, mapLinks, mapLocation } from '@/entities/admin/model/mapPrimitives'
import { mapBasketModifiers } from '@/entities/basket/model/mapBasketItem'
import { mapOrderPaymentFields } from '@/entities/payment/model/orderPayment'
import { mapUtm } from '@/entities/utm/model/mapUtm'

function mapClient(raw = {}) {
  const firstName = String(raw.first_name ?? '').trim()
  const lastName = String(raw.last_name ?? '').trim()
  const email = String(raw.email ?? '').trim()
  const phone = String(raw.phone_number ?? '').trim()
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || email || phone || ''

  return {
    id: String(raw.id ?? ''),
    firstName,
    lastName,
    email,
    phone,
    displayName,
    links: mapLinks(raw.links),
    utm: mapUtm(raw.utm),
  }
}

function mapRestaurant(raw = {}) {
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? '').trim(),
    address: asNullableText(raw.address) ?? '',
    supportTelegramUrl: asNullableText(raw.support_telegram_url),
  }
}

function mapCourier(raw = {}) {
  return {
    id: String(raw.id ?? ''),
    firstName: String(raw.first_name ?? ''),
    lastName: String(raw.last_name ?? '')
  }
}

function mapAdminOrderActionField(raw = {}) {
  return {
    name: String(raw.name ?? '').trim(),
    type: String(raw.type ?? 'string').trim() || 'string',
    optional: Boolean(raw.optional),
  }
}

function mapAdminOrderAction(raw = {}) {
  return {
    key: String(raw.key ?? '').trim(),
    role: String(raw.role ?? '').trim(),
    label: String(raw.label ?? '').trim(),
    method: String(raw.method ?? 'POST').trim().toUpperCase() || 'POST',
    path: String(raw.path ?? '').trim(),
    fields: Array.isArray(raw.fields)
      ? raw.fields.map(mapAdminOrderActionField).filter((field) => field.name)
      : [],
  }
}

function mapAdminOrderItems(items) {
  if (!Array.isArray(items)) {
    return []
  }

  return items.map((item) => ({
    id: String(item.product_id ?? ''),
    title: String(item.title ?? ''),
    quantity: Math.max(1, Math.floor(Number(item.quantity) || 1)),
    modifiers: mapBasketModifiers(item.modifiers),
  }))
}

function mapOrderPromoFields(raw = {}) {
  return {
    promoCode: String(raw.promo_code ?? '').trim() || null,
    promoCodeId: String(raw.promo_code_id ?? '').trim() || null,
    discountVnd: asFiniteNumber(raw.discount_vnd) ?? 0,
  }
}

export function mapAdminOrderListItem(raw) {
  if (!raw) {
    return null
  }

  return {
    id: String(raw.id ?? ''),
    orderId: String(raw.id ?? ''),
    clientId: String(raw.client?.id ?? ''),
    restaurantId: String(raw.restaurant?.id ?? ''),
    status: String(raw.status ?? ''),
    restaurant: String(raw.restaurant?.title ?? ''),
    createdAt: raw.created_at ?? null,
    updatedAt: raw.updated_at ?? null,
    timestamps: raw.timestamps ?? {},
    totalAmount: Number(raw.total_price ?? 0),
    paymentMethod: mapOrderPaymentFields(raw).paymentMethod,
    ...mapOrderPromoFields(raw),
    client:
      [raw.client?.first_name, raw.client?.last_name].filter(Boolean).join(' ').trim() ||
      String(raw.client?.email ?? raw.client?.phone_number ?? ''),
    clientPhone: String(raw.client?.phone_number ?? ''),
    deliveryAddress: String(raw.address ?? ''),
    itemsSummary: Number(raw.items_count ?? 0),
    courierId: raw.courier ? String(raw.courier.id ?? '') : '',
    courier: raw.courier
      ? [raw.courier.first_name, raw.courier.last_name].filter(Boolean).join(' ').trim() || String(raw.courier.id ?? '')
      : '',
    utm: mapUtm(raw.client?.utm),
  }
}

export function mapAdminOrderList(dtos) {
  if (!Array.isArray(dtos)) {
    return []
  }
  return dtos.map(mapAdminOrderListItem).filter(Boolean)
}

export function mapAdminOrderDetailToListItem(detail) {
  if (!detail) {
    return null
  }

  const client = detail.client
  const restaurant = detail.restaurant
  const courier = detail.courier
  const clientName = client
    ? [client.firstName, client.lastName].filter(Boolean).join(' ').trim() ||
      String(client.displayName ?? client.email ?? client.phone ?? '')
    : ''

  return {
    id: String(detail.id ?? ''),
    orderId: String(detail.id ?? ''),
    clientId: String(client?.id ?? ''),
    restaurantId: String(restaurant?.id ?? ''),
    status: String(detail.status ?? ''),
    restaurant: String(restaurant?.title ?? ''),
    createdAt: detail.createdAt ?? null,
    updatedAt: detail.updatedAt ?? null,
    timestamps: detail.timestamps ?? {},
    totalAmount: Number(detail.totalAmount ?? 0),
    paymentMethod: detail.paymentMethod ?? null,
    promoCode: detail.promoCode ?? null,
    promoCodeId: detail.promoCodeId ?? null,
    discountVnd: Number.isFinite(detail.discountVnd) ? detail.discountVnd : 0,
    client: clientName,
    clientPhone: String(client?.phone ?? ''),
    deliveryAddress: String(detail.deliveryAddress ?? ''),
    itemsSummary: Array.isArray(detail.items) ? detail.items.length : 0,
    courierId: courier ? String(courier.id ?? '') : '',
    courier: courier
      ? [courier.firstName, courier.lastName].filter(Boolean).join(' ').trim() || String(courier.id ?? '')
      : '',
    utm: client?.utm ?? null,
  }
}

export function mapAdminOrderDetail(raw) {
  if (!raw) {
    return null
  }

  const address = raw.address != null ? String(raw.address).trim() : ''

  return {
    id: String(raw.id ?? ''),
    status: String(raw.status ?? ''),
    createdAt: raw.created_at ?? null,
    updatedAt: raw.updated_at ?? null,
    timestamps: raw.timestamps ?? {},
    totalAmount: Number(raw.total_price ?? 0),
    deliveryFee: Number(raw.delivery_fee ?? 0),
    deliveryAddress: address,
    location: mapLocation(raw.location),
    pickupReadyAt: raw.pickup_ready_at ?? null,
    items: mapAdminOrderItems(raw.items),
    client: mapClient(raw.client),
    restaurant: mapRestaurant(raw.restaurant),
    courier: raw.courier ? mapCourier(raw.courier) : null,
    ...mapOrderPromoFields(raw),
    actions: Array.isArray(raw.actions)
      ? raw.actions.map(mapAdminOrderAction).filter((action) => action.key && action.path)
      : [],
    settlement: mapAdminOrderSettlement(raw.settlement),
    ...mapOrderPaymentFields(raw),
  }
}

function asFiniteNumber(value) {
  const amount = Number(value)
  return Number.isFinite(amount) ? amount : null
}

function mapSettlementBreakdown(raw) {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  return {
    orderVnd: asFiniteNumber(raw.order_vnd),
    restoVnd: asFiniteNumber(raw.resto_vnd),
    commissionVnd: asFiniteNumber(raw.commission_vnd),
    deliveryIncomeVnd: asFiniteNumber(raw.delivery_income_vnd),
    amountDueVnd: asFiniteNumber(raw.amount_due_vnd),
    discountVnd: asFiniteNumber(raw.discount_vnd),
    clientAmountDueVnd: asFiniteNumber(raw.client_amount_due_vnd),
    commissionRate: asFiniteNumber(raw.commission_rate),
  }
}

function mapAdminOrderSettlement(raw) {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  const status = String(raw.status ?? 'none').trim() || 'none'
  const breakdown = mapSettlementBreakdown(raw.breakdown)
  const amountVnd = Number(raw.amount_vnd)
  const expenseAmount = Number(raw.delivery_expense_amount)
  const partnerAmountVnd = Number(raw.partner_amount_vnd)
  const promoAmountVnd = asFiniteNumber(raw.promo_amount_vnd)
  const promoFunded = Boolean(raw.promo_funded)
  const promoFundedAt = raw.promo_funded_at ?? null

  if (status === 'none') {
    if (!breakdown && promoAmountVnd == null && !raw.promo_funded) {
      return null
    }
    return { status: 'none', breakdown, promoAmountVnd, promoFunded, promoFundedAt }
  }

  return {
    status,
    breakdown,
    promoAmountVnd,
    promoFunded,
    promoFundedAt,
    frozenAt: raw.frozen_at ?? null,
    settledAt: raw.settled_at ?? null,
    amountVnd: Number.isFinite(amountVnd) ? amountVnd : null,
    restoPaid: Boolean(raw.resto_paid),
    restoPaidAt: raw.resto_paid_at ?? null,
    commissionPaid: Boolean(raw.commission_paid),
    commissionPaidAt: raw.commission_paid_at ?? null,
    deliveryIncomePaid: Boolean(raw.delivery_income_paid),
    deliveryIncomePaidAt: raw.delivery_income_paid_at ?? null,
    deliveryExpensePaid: Boolean(raw.delivery_expense_paid),
    deliveryExpensePaidAt: raw.delivery_expense_paid_at ?? null,
    deliveryExpenseAmount: Number.isFinite(expenseAmount) ? expenseAmount : null,
    deliveryExpenseCurrency: String(raw.delivery_expense_currency ?? '').trim().toLowerCase() || null,
    partnerPaid: Boolean(raw.partner_paid),
    partnerPaidAt: raw.partner_paid_at ?? null,
    partnerAmountVnd: Number.isFinite(partnerAmountVnd) ? partnerAmountVnd : null,
  }
}
